import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import styles from '../WeeklyBreakdownCharts.module.scss';
import type { WeeklyBreakdownReport, WeekDayKey } from 'model/BookingsWeeklyBreakdownReport';
import { Spinner } from 'components/UI';

type Point = {
    dayKey: WeekDayKey;
    day: string;
    date: string;
    value: number;
    percent: number;
};

const DAY_ORDER: Array<{ key: WeekDayKey; label: string }> = [
    { key: 'sun', label: 'Sunday' },
    { key: 'mon', label: 'Monday' },
    { key: 'tue', label: 'Tuesday' },
    { key: 'wed', label: 'Wednesday' },
    { key: 'thu', label: 'Thursday' },
    { key: 'fri', label: 'Friday' },
    { key: 'sat', label: 'Saturday' }
];

const BAR_COLOR = '#BAA997';

interface Props {
    report: WeeklyBreakdownReport;
    loading: boolean;
}

export const TotalBookingsBarChart: React.FC<Props> = ({ report, loading }) => {
    const data: Point[] = React.useMemo(() => {
        if (!report) return [];

        return DAY_ORDER.map(({ key, label }) => {
            const value = report.totalBookings?.[key] ?? 0;
            const percent = report.bookingsPct?.[key] ?? 0;

            const date = report.days?.[key].format('[[]MM/DD/YYYY[]]');

            return {
                dayKey: key,
                day: label,
                date,
                value,
                percent
            };
        });
    }, [report]);

    const ValuePercentLabel = React.useCallback(
        ({ x, y, width, value, index }: any) => {
            const cx = (x ?? 0) + (width ?? 0) / 2;
            const p = data[index]?.percent ?? 0;

            return (
                <g>
                    <text
                        x={cx}
                        y={(y ?? 0) - 20}
                        textAnchor="middle"
                        fill="#3a3a3a"
                        fontWeight="600"
                    >
                        {value}
                    </text>
                    <text
                        x={cx}
                        y={(y ?? 0) - 5}
                        textAnchor="middle"
                        fill="#9b9b9b"
                        fontSize="12"
                    >
                        {p}%
                    </text>
                </g>
            );
        },
        [data]
    );

    const DayTick = React.useCallback(
        (props: any) => {
            const { x = 0, y = 0, payload } = props ?? {};
            const day = payload?.value as string | undefined;

            if (!day) return <g />;

            const item = data.find(d => d.day === day);
            const date = item?.date ?? '[MM/DD/YYYY]';

            return (
                <g transform={`translate(${x},${y})`}>
                    <text
                        dy={16}
                        textAnchor="middle"
                        fill="#2b2b2b"
                        fontWeight="700"
                    >
                        {day}
                    </text>
                    <text
                        dy={34}
                        textAnchor="middle"
                        fill="#9b9b9b"
                        fontSize="12"
                    >
                        {date}
                    </text>
                </g>
            );
        },
        [data]
    );

    return (
        <div className={styles.chartCard}>
            <h3 className={styles.title}>Total Bookings</h3>

            <div className={styles.barWrap}>
                {loading ? (
                    <div className={styles.loadingCell}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <ResponsiveContainer
                        width="100%"
                        height={320}
                    >
                        <BarChart
                            data={data}
                            margin={{ top: 40, right: 20, left: 20, bottom: 40 }}
                        >
                            <CartesianGrid
                                stroke="#eee"
                                vertical={false}
                            />

                            <XAxis
                                dataKey="day"
                                tick={DayTick}
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                width={32}
                                tick={{ fill: '#9b9b9b' }}
                                domain={[0, (max: number) => Math.ceil(max * 1.1)]}
                            />

                            <Bar
                                dataKey="value"
                                fill={BAR_COLOR}
                                radius={[8, 8, 0, 0]}
                                maxBarSize={64}
                            >
                                <LabelList
                                    dataKey="value"
                                    content={ValuePercentLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default TotalBookingsBarChart;
